import { Alert, Button, Card, List, Space, Tag, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import type { TFunction } from 'i18next';
import type { DailySchedulePlan, ScheduleFixVehicleItem } from '@ubus/shared';
import { listScheduleFixReport } from '@ubus/shared';
import { translateScheduleIssues } from '../i18n/translateErrors';

const { Text } = Typography;

export interface ScheduleFixPanelProps {
  plan: DailySchedulePlan;
  t: TFunction;
  onSelectVehicle: (vehicleId: string) => void;
  onQuickReassign: (jobId: string, vehicleId: string) => void;
  onUnassign: (jobId: string) => void;
  onOpenPublishedTrips?: (vehicleFleet?: string) => void;
}

function fleetLabel(item: ScheduleFixVehicleItem): string {
  return item.fleetNumber ?? item.vehicleId.slice(0, 8);
}

function reasonText(item: ScheduleFixVehicleItem, t: TFunction): string {
  return (
    translateScheduleIssues(item.issues.slice(0, 1), t) ||
    item.errors[0] ||
    t('schedule.fixPanelUnknownReason')
  );
}

export function ScheduleFixPanel({
  plan,
  t,
  onSelectVehicle,
  onQuickReassign,
  onUnassign,
  onOpenPublishedTrips,
}: ScheduleFixPanelProps) {
  const { blocking, publishedWarnings } = listScheduleFixReport(plan);

  if (blocking.length === 0 && publishedWarnings.length === 0) return null;

  return (
    <div id="schedule-fix-panel" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {blocking.length > 0 && (
        <Card
          className="admin-inner-card"
          size="small"
          title={
            <Space>
              <WarningOutlined style={{ color: '#cf1322' }} />
              <span>{t('schedule.fixPanelTitle')}</span>
              <Tag color="error">
                {t('schedule.fixPanelBlockingCount', { count: blocking.length })}
              </Tag>
            </Space>
          }
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {t('schedule.fixPanelHint')}
          </Text>
          <List
            size="small"
            dataSource={blocking}
            renderItem={(item) => (
              <List.Item
                style={{
                  display: 'block',
                  background: 'rgba(255, 77, 79, 0.04)',
                  borderRadius: 8,
                  padding: '12px 14px',
                  marginBottom: 8,
                }}
              >
                <Button
                  type="link"
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => onSelectVehicle(item.vehicleId)}
                >
                  <Text strong>
                    {fleetLabel(item)} · {t('schedule.fixPanelProblem')}
                  </Text>
                </Button>
                <Text type="secondary" style={{ display: 'block', margin: '4px 0 8px', fontSize: 13 }}>
                  {reasonText(item, t)}
                </Text>
                <Text style={{ fontSize: 12, fontWeight: 600 }}>{t('schedule.fixPanelDraftJobs')}</Text>
                <List
                  size="small"
                  dataSource={item.draftJobs}
                  split={false}
                  renderItem={(job) => {
                    const recFleet =
                      job.recommended?.fleetNumber ?? job.recommended?.vehicleId.slice(0, 8);
                    const km =
                      job.recommended?.deadheadKm != null
                        ? job.recommended.deadheadKm.toFixed(1)
                        : null;
                    return (
                      <List.Item
                        style={{ padding: '6px 0' }}
                        actions={[
                          job.recommended ? (
                            <Button
                              key="quick"
                              type="primary"
                              size="small"
                              onClick={() =>
                                onQuickReassign(job.jobId, job.recommended!.vehicleId)
                              }
                            >
                              {t('schedule.fixPanelQuickReassign', { fleet: recFleet })}
                            </Button>
                          ) : (
                            <Text key="none" type="secondary" style={{ fontSize: 12 }}>
                              {t('schedule.fixPanelNoFeasible')}
                            </Text>
                          ),
                          <Button key="rm" size="small" onClick={() => onUnassign(job.jobId)}>
                            {t('schedule.fixPanelRemove')}
                          </Button>,
                        ]}
                      >
                        <List.Item.Meta
                          title={<Text style={{ fontSize: 13 }}>{job.title}</Text>}
                          description={
                            job.recommended && km != null
                              ? t('schedule.fixPanelRecommendMeta', { fleet: recFleet, km })
                              : undefined
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {publishedWarnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={t('schedule.publishedWarnTitle', { count: publishedWarnings.length })}
          description={
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                {t('schedule.publishedWarnHint')}
              </Text>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {publishedWarnings.map((item) => (
                  <li key={item.vehicleId} style={{ marginBottom: 4 }}>
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto' }}
                      onClick={() => onSelectVehicle(item.vehicleId)}
                    >
                      {fleetLabel(item)}
                    </Button>
                    <Text type="secondary"> — {reasonText(item, t)}</Text>
                  </li>
                ))}
              </ul>
              {onOpenPublishedTrips ? (
                <Button
                  size="small"
                  style={{ marginTop: 8 }}
                  onClick={() =>
                    onOpenPublishedTrips(publishedWarnings[0]?.fleetNumber)
                  }
                >
                  {t('schedule.publishedWarnOpenTrips')}
                </Button>
              ) : null}
            </div>
          }
        />
      )}
    </div>
  );
}

export default ScheduleFixPanel;
